import { WordItem } from '../types';

/**
 * Normalizes strings by lowercasing, stripping accents and punctuation for reliable dictionary lookup.
 */
function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .replace(/œ/g, 'oe')
    .replace(/æ/g, 'ae')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove diacritics / accents
    .replace(/['’`\-_.,!?;:()\/\\«»“”&+]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Master dictionary map of exact French / Kabyle word representations to their perfect emoji icons.
 */
const EXACT_ICON_MAP: Record<string, string> = {
  // --- ANIMAUX ---
  'chien': '🐕',
  'chat': '🐈',
  'coq': '🐓',
  'poulet': '🐓',
  'coq poulet': '🐓',
  'poule': '🐔',
  'vache': '🐄',
  'mouton': '🐑',
  'moutons': '🐑',
  'loup': '🐺',
  'chacal': '🐺',
  'loup chacal': '🐺',
  'aigle': '🦅',
  'cheval': '🐎',
  'ane': '🫏',
  'lapin': '🐇',
  'sanglier': '🐗',
  'oiseau': '🐦',
  'poisson': '🐟',
  'mouche': '🪰',
  'abeille': '🐝',
  'fourmi': '🐜',
  'souris': '🐭',

  // --- LA MAISON & OBJETS ---
  'maison': '🏠',
  'maison grande': '🏡',
  'porte': '🚪',
  'cle': '🔑',
  'foyer cheminee': '🔥',
  'foyer': '🔥',
  'cheminee': '🔥',
  'mur': '🧱',
  'chambre': '🚪',
  'chambre salle': '🚪',
  'pilier': '🏛️',
  'toit': '🏠',
  'tasse': '☕',
  'assiette': '🍽️',
  'cuillere': '🥄',
  'fourchette': '🍴',
  'couteau': '🔪',
  'poutre': '🪵',
  'lit': '🛏️',
  'oreiller': '🛋️',
  'couverture': '🧶',
  'miroir': '🪞',
  'bouteille': '🍾',
  'ouvre la porte': '🚪',
  'ferme la porte': '🚪',

  // --- LE CORPS & SOINS ---
  'pantalon': '👖',
  'robe': '👗',
  'chaussures': '👞',
  'chemise': '👔',
  'chaussettes': '🧦',
  'savon': '🧼',
  'laver': '🧼',
  'proprete': '✨',
  'dent': '🦷',
  'dents': '🦷',
  'peigne': '🪮',
  'langue': '👅',
  'orteil': '🦶',
  'j ai faim': '🤤',
  'j ai soif': '🥤',
  'j ai sommeil': '🥱',
  'je suis malade': '🤒',
  'coeur': '❤️',
  'tete': '👤',
  'tete affectueux': '👤',
  'main': '✋',
  'pied': '🦶',
  'yeux': '👀',
  'oeil': '👁️',
  'bouche': '👄',
  'oreille': '👂',
  'front': '👤',
  'nez': '👃',
  'joue': '😊',
  'barbe': '🧔',
  'poitrine': '🫁',
  'dos': '🧍',
  'bras': '💪',
  'comment est ta sante': '🩺',

  // --- NOURRITURE ---
  'lait': '🥛',
  'miel': '🍯',
  'couscous': '🍲',
  'sel': '🧂',
  'sucre': '🍬',
  'pomme': '🍎',
  'figues': '🫐',
  'raisin': '🍇',
  'oignon': '🧅',
  'carotte': '🥕',
  'pain': '🥖',
  'mange du pain': '🥖',
  'viande': '🥩',
  'pomme de terre': '🥔',
  'apporte un cafe': '☕',
  'viens manger': '🍽️',
  'mange le maintenant': '🍽️',
  'donne de l eau': '💧',
  'c est delicieux': '😋',
  'bon appetit sante': '🍽️',
  'bon appetit': '🍽️',

  // --- NATURE & METEO ---
  'riviere': '🏞️',
  'colline': '⛰️',
  'echo': '📢',
  'eau': '💧',
  'lumiere': '💡',
  'obscurite': '🌑',
  'ciel meteo': '⛅',
  'ciel': '🌌',
  'etoile': '⭐',
  'etincelle': '✨',
  'source eau': '⛲',
  'source': '⛲',
  'mare': '💧',
  'foret': '🌲',
  'herbe': '🌿',
  'fleur': '🌸',
  'arbre': '🌳',
  'arbre branche': '🌿',
  'branche': '🌿',
  'huile': '🫒',
  'olivier olives': '🫒',
  'olivier': '🫒',
  'olives': '🫒',
  'soleil': '☀️',
  'terre': '🌍',
  'neige': '❄️',
  'vent': '💨',
  'froid': '🥶',
  'froid climat': '🥶',
  'chaud climat': '🥵',
  'chaud': '🥵',
  'il fait froid aujourd hui': '🥶',
  'pluie': '🌧️',
  'averse': '⛈️',
  'nuage': '☁️',
  'gel givre': '🧊',
  'gel': '🧊',
  'givre': '🧊',
  'brouillard': '🌫️',
  'cailloux': '🪨',
  'caillou': '🪨',
  'chene': '🌳',

  // --- NOMBRES & QUANTITE ---
  'un': '1️⃣',
  'deux': '2️⃣',
  'trois': '3️⃣',
  'quatre': '4️⃣',
  'cinq': '5️⃣',
  'six': '6️⃣',
  'sept': '7️⃣',
  'huit': '8️⃣',
  'neuf': '9️⃣',
  'dix': '🔟',
  'beaucoup': '📈',
  'peu': '📉',
  'un petit peu': '🤏',
  'un peu': '🤏',
  'quelques': '🔢',
  'part morceau': '🍰',
  'argent monnaie': '💰',
  'argent': '💰',

  // --- COULEURS ---
  'rouge': '🔴',
  'vert': '🟢',
  'jaune': '🟡',
  'blanc': '⚪',
  'noir': '⚫',
  'bleu': '🔵',
  'marron': '🟤',
  'bleu clair gris': '🔷',
  'bleu clair': '🔷',
  'violet fonce': '🟣',
  'violet': '🟣',
  'rose': '🌸',
  'orange': '🟠',
  'gris': '🔘',

  // --- JEUX & TRANSPORTS ---
  'voiture': '🚗',
  'bateau': '⛵',
  'avion': '✈️',
  'train': '🚆',
  'chemin de fer': '🛤️',
  'ballon': '⚽',
  'jeu jouer': '🎮',
  'jeu': '🎮',
  'jouer': '🎮',
  'poupee': '🪆',
  'course': '🏃',
  'sauter': '🦘',
  'ou est le taxi': '🚕',
  'taxi': '🚕',
  'passe moi le pc': '💻',

  // --- LA FAMILLE ---
  'bebe': '👶',
  'grand mere': '👵',
  'grand pere': '👴',
  'oncle maternel': '👨',
  'oncle paternel': '👨',
  'tante maternelle': '👩',
  'homme': '👨',
  'femme': '👩',
  'garcon': '👦',
  'fille': '👧',
  'vieux sage': '👴',
  'vieille femme': '👵',
  'mon fils': '👦',
  'ma fille': '👧',
  'mon frere appel': '👦',
  'mon frere': '👦',
  'ma soeur appel': '👧',
  'ma soeur': '👧',
  'mon amour mon foie': '💖',

  // --- LIEUX & ESPACE ---
  'pays': '🗺️',
  'village': '🏘️',
  'chemin': '🛣️',
  'c est loin': '🏔️',
  'c est proche': '📍',
  'ici': '📍',
  'la bas': '👉',
  'au dela': '🌅',
  'en haut': '⬆️',
  'en bas': '⬇️',
  'droite': '➡️',
  'gauche': '⬅️',
  'devant': '🔼',
  'derriere': '🔽',
  'par la': '↗️',
  'bejaia': '🌊',
  'tizi ouzou': '🏔️',
  'bouira': '🌾',
  'alger': '🏙️',
  'kabylie': 'ⵣ',
  'hopital': '🏥',
  'ou est l hotel': '🏨',
  'le chemin vers': '🛣️',
  'votre pays est beau': '🏞️',
  'bienvenue au pays': '🏔️',

  // --- LE TEMPS ---
  'jour moment': '☀️',
  'jour': '☀️',
  'nuit': '🌙',
  'matin': '🌅',
  'soir': '🌆',
  'parfois': '⏳',
  'cette fois': '⏱️',
  'fois': '🔄',
  'temps': '⏳',
  'heure': '⏰',
  'autrefois': '📜',
  'avant': '⏪',
  'apres': '⏩',
  'toujours': '♾️',
  'aujourd hui': '📅',
  'demain': '🌅',
  'hier': '🗓️',
  'chaque jour': '🔄',
  'en ce temps la': '🕰️',
  'maintenant': '⚡',
  'quelle heure est il': '⏰',
  'je reviens de suite': '⏱️',
  'on verra demain': '🌅',

  // --- HISTOIRES, ECOLE & BOSSWORDS ---
  'ecole': '🏫',
  'stylo': '🖊️',
  'livre': '📖',
  'cahier': '📓',
  'classe': '🏫',
  'roi': '👑',
  'reine': '👑',
  'professeur': '👨‍🏫',
  'professeure': '👩‍🏫',
  'eleve': '🧑‍🎓',
  'ecriture': '✍️',
  'lecture': '📖',
  'conte': '📜',
  'conte legende': '📜',
  'histoire conte': '📜',
  'histoire discipline': '🏛️',
  'proverbe': '💬',
  'aventures': '🧭',
  'poemes': '📜',
  'chanson': '🎵',
  'la sagesse': '🦉',
  'sagesse': '🦉',
  'la paix': '🕊️',
  'paix': '🕊️',
  'la bravoure': '🛡️',
  'bravoure': '🛡️',
  'l amour': '💖',
  'amour': '💖',
  'l espoir': '🌟',
  'espoir': '🌟',

  // --- ACTIONS & VERBES ---
  'j ai achete': '🛒',
  'tu as achete': '🛒',
  'j ai mange': '🍽️',
  'tu as mange': '🍽️',
  'il a mange': '🍽️',
  'elle a mange': '🍽️',
  'nous avons mange': '🍽️',
  'manger': '🍽️',
  'je mangerai': '🍽️',
  'tu mangeras': '🍽️',
  'nous mangerons': '🍽️',
  'j ai bu': '🥛',
  'tu as bu': '🥛',
  'il a bu': '🥛',
  'elle a bu': '🥛',
  'nous avons bu': '🥛',
  'boire': '🥤',
  'je boirai': '🥤',
  'tu boiras': '🥤',
  'nous boirons': '🥤',
  'je suis parti': '🚶',
  'tu es parti': '🚶',
  'il est parti': '🚶',
  'elle est partie': '🚶',
  'nous sommes partis': '🚶',
  'pars': '🚶',
  'marcher': '🚶',
  'j irai': '🚶',
  'tu iras': '🚶',
  'nous irons': '🚶',
  'j ai travaille': '🔨',
  'tu as travaille': '🔨',
  'il a travaille': '🔨',
  'elle a travaille': '🔨',
  'nous avons travaille': '💼',
  'je travaillerai': '💼',
  'tu travailleras': '💼',
  'nous travaillerons': '💼',
  'je veux etat': '💭',
  'tu veux': '💭',
  'il veut': '💭',
  'elle veut': '💭',
  'nous voulons': '💭',
  'il a voulu': '💭',
  'j ai trouve decouvert': '🔍',
  'j ai trouve': '💡',
  'tu as trouve': '💡',
  'je me suis reveille': '⏰',
  'tu t es reveille': '⏰',
  'je suis arrive': '🏁',
  'tu es arrive': '🏁',
  'apporte moi': '🤲',
  'donne moi': '🎁',
  'elle a donne': '🎁',
  'il a donne': '🎁',
  'assieds toi': '🪑',
  'je suis reste': '🪑',
  'tu es reste': '🪑',
  'tais toi': '🤫',
  'attends': '⏳',
  'envoyer': '✉️',
  'j ai regarde': '👁️',
  'tu as regarde': '👁️',
  'regarde': '👁️',
  'vois regarde': '👁️',
  'je vois actuellement': '👁️',
  'tu vois': '👁️',
  'j ai vu': '👁️',
  'tu as vu': '👁️',
  'je l ai vu': '👁️',
  'je suis entre': '🚪',
  'tu es entre': '🚪',
  'entrer': '🚪',
  'je suis revenu': '🔙',
  'tu es revenu': '🔙',
  'je suis sorti': '🚪',
  'tu es sorti': '🚪',
  'sortir': '🚪',
  'leve toi': '🧍',
  'je me suis leve': '🧍',
  'tu t es leve': '🧍',
  'prends tiens': '✊',
  'rends': '🔄',
  'parle': '🗣️',
  'j ai parle': '🗣️',
  'ecoute': '👂',
  'j ai entendu': '👂',
  'tu as entendu': '👂',
  'j ai oublie': '💭',
  'tu as oublie': '💭',
  'je l ai oublie': '💭',
  'casser': '💥',
  'ecris': '✍️',
  'lis': '📖',
  'dormir': '😴',
  'j ai jure': '✋',
  'tu as jure': '✋',
  'j ai compris': '💡',
  'tu as compris': '💡',
  'je sais': '🧠',
  'tu sais': '🧠',
  'je me suis rappele': '💡',
  'je me souviens': '🧠',
  'donne un coup de main': '🤝',
  'il a refuse': '🙅‍♂️',
  'je l ai fait': '✅',
  'tu l ai fait': '✅',
  'tu l as fait': '✅',

  // --- EMOTIONS & SENSATIONS ---
  'je serai': '✨',
  'tu seras': '✨',
  'nous serons': '✨',
  'j etais': '⏳',
  'tu etais': '⏳',
  'ce qui est bien': '👍',
  'c est une bonne chose': '👍',
  'c est bien beau': '🌺',
  'celui ci': '👉',
  'tu remplis mon coeur': '💖',
  'nouveau': '✨',
  'ancien vieux': '🏺',
  'bon bien': '👍',
  'mauvais': '👎',
  'c est bien bon': '👍',
  'c est mauvais': '👎',
  'c est beau': '🌺',
  'c est difficile': '🧗',
  'c est facile': '👌',
  'c est leger': '🪶',
  'c est lourd': '🪨',
  'c est profond': '🌊',
  'c est long': '📏',
  'c est court': '📐',
  'c est grand': '🏰',
  'c est petit': '🐥',
  'c est super top': '🌟',
  'impossible': '🚫',
  'lourd lent': '🏋️',
  'leger rapide': '🪶',
  'lourd': '🪨',
  'leger': '🪶',
  'joie fete': '🎉',
  'la joie': '🎉',
  'etre joyeux': '😄',
  'charmant mignon': '🥰',
  'haut eleve': '🏔️',
  'affame': '🤤',
  'assoiffe': '🥤',
  'aimer': '❤️',
  'etre triste': '😢',
  'avoir peur': '😨',
  'peur': '😨',
  'fatigue': '😫',
  'il est fatigue': '😫',
  'unique seul': '🧍',
  'peine souci': '😔',
  'jalousie': '😒',
  'il est en colere': '😡',
  'malheureux': '😔',
  'rapide': '⚡',
  'beaute': '✨',
  'piquant douleur': '⚡',
  'intelligent malin': '🦊',
  'affectueux tendre': '🤗',
  'simple modeste': '🌾',
  'amer': '☕',
  'dur': '🪨',
  'doux': '🪶',
  'casse': '💥',

  // --- DIALOGUES & SALUTATIONS ---
  'bonjour': '👋',
  'au revoir': '👋',
  'bonne nuit': '🌙',
  'bonne journee': '☀️',
  'merci': '🙏',
  'merci courant': '🙏',
  's il te plait': '🙏',
  'je t en prie': '🙏',
  'excuse moi': '🙇',
  'bienvenue a vous': '🤝',
  'oui': '✅',
  'non': '❌',
  'd accord': '👍',
  'de rien': '😊',
  'ca suffit': '✋',
  'comment vas tu': '😊',
  'comment ca va': '😊',
  'quel est ton nom m': '🏷️',
  'quel est ton nom f': '🏷️',
  'mon nom est': '🏷️',
  'quoi de neuf': '💬',
  'ou vas tu': '🚶',
  'depeche toi': '🏃',
  'parle doucement': '🤫',
  'repete s il te plait': '🔁',
  'je ne comprends pas': '❓',
  'comment dit on': '🗣️',
  'quoi': '❓',
  'ou': '📍',
  'comment': '🤔',
  'quand': '📅',
  'qui': '👤',
  'combien': '🔢',
  'pourquoi': '❓',
  'qu est ce que c est': '🔍',
  'c est vrai': '✔️',
  'ca me plait ici': '🥰',
  'je suis tres content ici': '😄',
  'c est ma premiere fois ici': '🌟',
  'emmene moi a': '🚗',
  'quel age as tu': '🎂',
  'qui est ce': '👤',
  'd ou viens tu': '🌍',
  'combien ca coute respect': '💰',
  'combien ca coute': '💰',
  'que me conseilles tu': '💡',
  'peux tu me montrer': '👉',
  'je ne connais pas le chemin': '🧭',
  'merci pour l accueil': '🤝',
  'que veux tu dire': '❓',
  'je ne sais pas': '🤷',
  'qu est ce que tu as': '😟',
  'allez on y va': '🚀',
  'laisse moi stp': '✋',
  'moi aussi': '🙋',
  'ne t inquiete pas': '😌',
  'ne t en fais pas': '😌',
  'tu as raison': '🎯',
  'tu m as compris': '💡',
  'je m ennuie': '🥱',
  'tu parles trop': '🗣️',
  'je rentre en france demain': '✈️',
  'je viens de france': '🇫🇷',
  'j apprends le kabyle': '📚',
  'peux tu m aider': '🤝',
  'ou puis je manger': '🍽️',
  'c est moi': '🙋',
  'c est toi m': '🫵',
  'c est toi f': '🫵',
  'c est toi': '🫵',
  'c est lui': '👤',
  'c est elle': '👤',
  'c est nous': '👥',
  'c est vous m': '👥',
  'c est vous': '👥',
  'ce sont eux': '👥',
  'ce sont elles': '👥',
  'qui a appele': '📞',
  'notre langue est riche': '💎',
  'fais confiance a dieu': '🤲',

  // --- MOTS DE LIAISON ---
  'donc': '➡️',
  'peut etre': '🤔',
  'meme si': '🔗',
  'pour que comme': '🔗',
  'pour que': '🔗',
  'comme': '🔗',
  'puisque': '🔗',
  'alors donc': '➡️',
  'alors': '➡️',
  'si seulement': '💭',
  'meme': '🔗',
  'jusqu a ce que': '⏳',

  // --- COMPLÉMENTS NATURE & UNIVERS ---
  'montagne': '⛰️',
  'montagnes': '🏔️',
  'djurdjura': '🏔️',
  'mer': '🌊',
  'ocean': '🌊',
  'desert': '🏜️',
  'sable': '🏜️',
  'lune': '🌙',
  'croissant': '🌙',
  'croissant de lune': '🌙',
  'terre monde': '🌍',
  'monde': '🌍',
  'univers': '🌌',
  'foudre': '⚡',
  'eclair': '⚡',
  'orage': '⛈️',
  'tempete': '🌪️',
  'arc en ciel': '🌈',
  'cascade': '🌊',
  'ruisseau': '🏞️',
  'grotte': '🪨',
  'plante': '🌱',
  'champignon': '🍄',
  'feuille': '🍃',
  'feuilles': '🍃',
  'rose fleur': '🌹',
  'tournesol': '🌻',
  'palmier': '🌴',
  'cactus': '🌵',
  'graine': '🌱',

  // --- COMPLÉMENTS ANIMAUX & FAUNE ---
  'lion': '🦁',
  'panthere': '🐆',
  'leopard': '🐆',
  'gazelle': '🦌',
  'cerf': '🦌',
  'chameau': '🐪',
  'dromadaire': '🐫',
  'singe': '🐒',
  'serpent': '🐍',
  'vipere': '🐍',
  'lezard': '🦎',
  'grenouille': '🐸',
  'tortue': '🐢',
  'herisson': '🦔',
  'hibou': '🦉',
  'chouette': '🦉',
  'faucon': '🦅',
  'pigeon': '🕊️',
  'colombe': '🕊️',
  'corbeau': '🐦‍⬛',
  'canard': '🦆',
  'oie': '🪿',
  'cygne': '🦢',
  'chauve souris': '🦇',
  'papillon': '🦋',
  'escargot': '🐌',
  'araignee': '🕷️',
  'scorpion': '🦂',
  'requin': '🦈',
  'dauphin': '🐬',
  'baleine': '🐋',
  'pieuvre': '🐙',
  'crabe': '🦀',
  'crevette': '🦐',

  // --- COMPLÉMENTS ALIMENTATION & BOISSONS ---
  'huile d olive': '🫒',
  'huile olive': '🫒',
  'galette': '🫓',
  'galette de pain': '🫓',
  'pain traditionnel': '🫓',
  'aghroum': '🫓',
  'semoule': '🌾',
  'ble': '🌾',
  'orge': '🌾',
  'farine': '🌾',
  'couscous kabyle': '🍲',
  'soupe': '🥣',
  'chorba': '🥣',
  'cafe': '☕',
  'the': '🍵',
  'the a la menthe': '🍵',
  'menthe': '🌿',
  'oeuf': '🥚',
  'oeufs': '🥚',
  'fromage': '🧀',
  'beurre': '🧈',
  'beurre traditionnel': '🧈',
  'huile piment': '🌶️',
  'piment': '🌶️',
  'poivre': '🧂',
  'tomate': '🍅',
  'citron': '🍋',
  'orange fruit': '🍊',
  'mandarine': '🍊',
  'grenade': '🫐',
  'datte': '🌴',
  'dattes': '🌴',
  'pasteque': '🍉',
  'melon': '🍈',
  'banane': '🍌',
  'peche': '🍑',
  'abricot': '🍑',
  'cerise': '🍒',
  'fraise': '🍓',
  'noix': '🥜',
  'amande': '🥜',
  'chataigne': '🌰',
  'ail': '🧄',
  'salade': '🥗',
  'gateau': '🍰',
  'crepe': '🥞',
  'bonbon': '🍬',
  'chocolat': '🍫',
  'jus': '🧃',

  // --- OBJETS TRADITIONNELS, MAISON & CULTURE BERBÈRE ---
  'bijou': '💍',
  'bijoux': '💍',
  'bague': '💍',
  'collier': '📿',
  'bracelet': '🪙',
  'fibule': '🔱',
  'argent berbere': '🪙',
  'robe kabyle': '👗',
  'tapis': '🧶',
  'tissage': '🧶',
  'metier a tisser': '🧵',
  'fil': '🧵',
  'aiguille': '🪡',
  'poterie': '🏺',
  'cruche': '🏺',
  'amphore': '🏺',
  'plat': '🍽️',
  'bol': '🥣',
  'marmite': '🍲',
  'chaudron': '🍲',
  'tajine': '🍲',
  'tamis': '🧺',
  'panier': '🧺',
  'lampe': '🪔',
  'lampe a huile': '🪔',
  'bougie': '🕯️',
  'fusil': '🔫',
  'epee': '⚔️',
  'bouclier': '🛡️',
  'tambour': '🥁',
  'flute': '🪈',
  'musique': '🎵',
  'drapeau': '🚩',
  'drapeau berbere': 'ⵣ',
  'lettre tifinagh': 'ⵣ',
  'yaz': 'ⵣ',
  'village berbere': '🏘️',
  'fontaine': '⛲',
  'place du village': '🏛️',
  'tajmaat': '🏛️',

  // --- ÉDUCATION, LIVRES, SCIENCES & TRAVAIL ---
  'crayon': '✏️',
  'gomme': '🧼',
  'tableau': '📋',
  'regle': '📏',
  'ciseaux': '✂️',
  'ordinateur': '💻',
  'telephone': '📱',
  'message': '💬',
  'lettre': '✉️',
  'medecin': '👨‍⚕️',
  'docteur': '👨‍⚕️',
  'infirmiere': '👩‍⚕️',
  'medicament': '💊',
  'pansement': '🩹',
  'outil': '🔧',
  'outils': '🛠️',
  'marteau': '🔨',
  'scie': '🪚',
  'hache': '🪓',
  'pelle': '⛏️',
  'agriculteur': '🧑‍🌾',
  'fermier': '🧑‍🌾',
  'berger': '🐑',
  'artisan': '🧑‍🔧',
  'macon': '🧱',
  'peintre': '🎨',
  'jardinier': '🪴',
  'chasseur': '🏹',
  'pecheur': '🎣',

  // --- VOYAGES, LIEUX & TRANSPORTS ---
  'velo': '🚲',
  'bicyclette': '🚲',
  'moto': '🏍️',
  'bus': '🚌',
  'autobus': '🚌',
  'camion': '🚛',
  'gare': '🚉',
  'aeroport': '🛫',
  'pont': '🌉',
  'tunnel': '🚇',
  'tente': '⛺',
  'chateau': '🏰',
  'mosquee': '🕌',
  'eglise': '⛪',
  'marche': '🏪',
  'magasin': '🏪',
  'boutique': '🏪',
  'souk': '🏪',
  'boussole': '🧭',
  'carte': '🗺️',
  'valise': '🧳',
  'sac': '🎒',
  'sac a dos': '🎒',

  // --- MOTS KABYLES COURANTS ---
  'itran': '⭐',
  'itri': '⭐',
  'iṭij': '☀️',
  'itij': '☀️',
  'aggur': '🌙',
  'ayur': '🌙',
  'tiziri': '🌕',
  'aman': '💧',
  'times': '🔥',
  'taddart': '🏘️',
  'taxxamt': '🚪',
  'tawurt': '🚪',
  'tawla': '🤒',
  'aksum': '🥩',
  'aɣrum': '🥖',
  'ayefki': '🥛',
  'tament': '🍯',
  'azemmur': '🫒',
  'tazemmurt': '🫒',
  'azal': '☀️',
  'id': '🌙',
  'iḍ': '🌙',
  'tafsut': '🌸',
  'anebdu': '☀️',
  'amenzuy': '🍂',
  'tagrest': '❄️',
  'aqcic': '👦',
  'taqcict': '👧',
  'argaz': '👨',
  'tameṭṭut': '👩',
  'tamettut': '👩',
  'amɣar': '👴',
  'amghar': '👴',
  'tamɣart': '👵',
  'tamghart': '👵',
  'aqjun': '🐕',
  'amcic': '🐈',
  'ayrad': '🦁',
  'uccen': '🐺',
  'izimer': '🐑',
  'tafunast': '🐄',
  'afrux': '🐦',
  'aslem': '🐟',
  'aɣyul': '🫏',
  'aghyul': '🫏',
  'ayis': '🐎',
  'tizizwit': '🐝',
  'awtul': '🐇',
  'ilef': '🐗',
  'izem': '🦁',
  'tayri': '💖',
  'talwit': '🕊️',
  'tilelli': '🕊️',
  'asirem': '🌟',
  'tussna': '🦉',
  'tamusni': '🦉',
  'tizlit': '🎵',
  'idles': '📜',
  'awal': '💬',
  'amawal': '📚',
  'taqbaylit': 'ⵣ',
  'tamaziɣt': 'ⵣ',
  'amazigh': 'ⵣ',
  'tamazight': 'ⵣ'
};

/**
 * Thematic emoji palette recommendations per category.
 * Used when no single high-confidence keyword match is found, or as diverse suggestions.
 */
export const CATEGORY_ICON_PRESETS: Record<string, string[]> = {
  'Animaux': ['🐾', '🐕', '🐈', '🐑', '🐄', '🐺', '🐎', '🫏', '🐇', '🐗', '🐦', '🦅', '🐟', '🐝', '🐜', '🦁', '🦌', '🕊️'],
  'La Maison': ['🏠', '🏡', '🚪', '🔑', '🛏️', '🧱', '🪞', '🛋️', '🪵', '🔥', '🕯️', '🪔', '☕', '🍽️', '🥄', '🔪', '🏺', '🧺'],
  'Nature & Météo': ['🌿', '🌳', '🌲', '🌸', '🫒', '💧', '☀️', '🌧️', '⛅', '☁️', '❄️', '💨', '⚡', '🌈', '⛰️', '🏔️', '🌊', '⭐', '🌙', '🌱', '🪨'],
  'Nombres & Quantité': ['🔢', '1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣', '🔟', '📈', '📉', '🤏', '💰', '🪙', '🍰', '⚖️'],
  'Le Corps & Soins': ['🩺', '❤️', '✋', '🦶', '👀', '👁️', '👂', '👃', '👄', '👅', '🦷', '💪', '🫁', '👤', '🧔', '🧠', '🧼', '🪮', '🩹', '💊'],
  'Verbes': ['🏃', '🚶', '🤸', '🧗', '🏊', '🚴', '🦘', '🛠️', '🔨', '✍️', '🛒', '🍽️', '🥤', '🚪', '🏁', '⚡', '💪'],
  "Verbes d'Action": ['🏃', '🚶', '🤸', '🧗', '🏊', '🚴', '🦘', '🛠️', '🔨', '✍️', '🛒', '🍽️', '🥤', '🚪', '🏁', '⚡', '💪'],
  'Pensée & Communication': ['🗣️', '💬', '🧠', '💡', '💭', '👂', '✍️', '📖', '📢', '🤫', '🤝', '🦉', '🎯', '📱', '✉️'],
  'Verbes au Passé': ['🕰️', '📜', '⏳', '⏪', '🏁', '✅', '🏺', '💡', '🔍', '🚪', '📦'],
  'Verbes au Futur & Présent': ['⚡', '🚀', '⏩', '🌟', '⏰', '📈', '🎯', '🔥', '✨', '🚶', '🌱'],
  'Émotions & Sensations': ['💜', '💖', '🥰', '😄', '😊', '🎉', '😢', '😨', '😡', '😫', '🤤', '🥤', '🥶', '🥵', '🌺', '✨', '🌟', '🕊️'],
  'Histoires & École': ['📚', '📖', '📜', '🏫', '✍️', '✏️', '📓', '👑', '🦉', '🕊️', '🛡️', '⚔️', '🎵', '🧭', '🌟', '💎', '🏺'],
  'Couleurs': ['🎨', '🔴', '🟢', '🟡', '🔵', '⚪', '⚫', '🟣', '🟠', '🟤', '🌸', '🔷', '🔘', '🌈', '✨'],
  'Le Temps': ['⏳', '⏰', '⏱️', '📅', '🗓️', '☀️', '🌙', '🌅', '🌆', '🕰️', '🔄', '♾️', '⚡'],
  'Salutations & Politesse': ['👋', '🙏', '🤝', '😊', '☀️', '🌙', '🙇', '👍', '✨', '💐', '🤲'],
  'Pronoms & Personnes': ['👤', '👥', '🫵', '🙋', '👨', '👩', '👦', '👧', '👴', '👵', '👶', '🧑‍🎓', '🧑‍🌾'],
  'Questions & Réponses': ['❓', '🔍', '🤔', '💡', '🗣️', '📍', '📅', '🔢', '🏷️', '🧭', '✅', '❌'],
  'Phrases & Quotidien': ['💬', '🗣️', '☕', '🥖', '🚗', '🚕', '💻', '📱', '🏠', '🚶', '🍽️', '🛒', '✨'],
  'La Famille': ['👨‍👩‍👧', '👶', '👦', '👧', '👨', '👩', '👴', '👵', '💖', '🏠', '🤝', '🫂'],
  'Nourriture': ['🍯', '🥛', '🥖', '🫓', '🍲', '🥩', '🍎', '🍇', '🫐', '🫒', '🥕', '🧅', '🥔', '🍳', '🥚', '🧀', '🧂', '🍬', '☕', '🍵', '🌶️', '🍉'],
  'Jeux & Transports': ['🚗', '🚕', '🚌', '🚆', '🚲', '⛵', '✈️', '⚽', '🎮', '🪆', '🦘', '🏃', '🛹', '🎳', '🎲', '🎯', '🏁'],
  'Lieux & Espace': ['🗺️', '🏔️', '🏘️', '🏙️', '🛣️', '🌊', '📍', '🏰', '⛺', '🏥', '🏪', '🌅', '🧭', 'ⵣ'],
  'Mots de liaison': ['🔗', '➡️', '↪️', '↔️', '➕', '⏳', '💭', '💡', '🎯', '🔍'],
  'Personnalisé': ['📝', '✨', '⭐', '💡', '🏷️', '🎨', '🎯', '🔥', '📚', '💖', 'ⵣ'],
  'Favoris': ['❤️', '💖', '⭐', '🌟', '💎', '🔥', '✨', '🎉', '🏆', '🎯']
};

/**
 * Universal default icons for fallback suggestions.
 */
export const POPULAR_UNIVERSAL_ICONS = ['⭐', '✨', '📝', '💡', '💖', 'ⵣ', '🌿', '🐾', '🏠', '🍽️', '📚', '🎯'];

/**
 * Multi-candidate suggestion engine:
 * Given a word item (French definition, Kabyle text, variants, category),
 * returns up to `limit` highly ranked, diverse emoji suggestions.
 */
export function getWordIconSuggestions(
  word: { kab?: string; fr?: string; variants?: string; type?: string } | null,
  limit: number = 8
): string[] {
  if (!word) return POPULAR_UNIVERSAL_ICONS.slice(0, limit);

  const suggestions: string[] = [];
  const seen = new Set<string>();

  const add = (emoji: string | null | undefined) => {
    if (!emoji) return;
    const clean = emoji.trim();
    if (!clean || seen.has(clean)) return;
    seen.add(clean);
    suggestions.push(clean);
  };

  // 1. Primary best-match icon
  const primaryMatch = getWordIcon({
    kab: word.kab || '',
    fr: word.fr || '',
    type: word.type || ''
  } as WordItem);
  if (primaryMatch && primaryMatch !== '✨' && primaryMatch !== '📝') {
    add(primaryMatch);
  }

  const rawFr = word.fr || '';
  const rawKab = word.kab || '';
  const rawVariants = word.variants || '';
  const normFr = normalizeText(rawFr);
  const normKab = normalizeText(rawKab);

  // 2. Direct lookup for segments in French definition (split by slashes, commas, parentheses)
  const frTokens = rawFr.split(/[/(),;:]+/).map(s => normalizeText(s)).filter(Boolean);
  for (const token of frTokens) {
    if (EXACT_ICON_MAP[token]) add(EXACT_ICON_MAP[token]);
    // Single words in token
    for (const singleWord of token.split(' ')) {
      if (singleWord.length > 2 && EXACT_ICON_MAP[singleWord]) {
        add(EXACT_ICON_MAP[singleWord]);
      }
    }
  }

  // 3. Kabyle tokens & variants lookup
  const kabTokens = `${rawKab} ${rawVariants}`.split(/[/(),;: -]+/).map(s => normalizeText(s)).filter(Boolean);
  for (const token of kabTokens) {
    if (EXACT_ICON_MAP[token]) add(EXACT_ICON_MAP[token]);
  }

  // 4. Keyword and regex matching on French text
  // Animals
  if (/\b(chien|chienne|chiot)\b/.test(normFr)) add('🐕');
  if (/\b(chat|chatte|chaton)\b/.test(normFr)) add('🐈');
  if (/\b(lion|lionne)\b/.test(normFr)) add('🦁');
  if (/\b(panthere|leopard)\b/.test(normFr)) add('🐆');
  if (/\b(gazelle|cerf|biche)\b/.test(normFr)) add('🦌');
  if (/\b(chameau|dromadaire)\b/.test(normFr)) add('🐪');
  if (/\b(singe)\b/.test(normFr)) add('🐒');
  if (/\b(serpent|vipere)\b/.test(normFr)) add('🐍');
  if (/\b(tortue)\b/.test(normFr)) add('🐢');
  if (/\b(grenouille)\b/.test(normFr)) add('🐸');
  if (/\b(coq|poulet)\b/.test(normFr)) add('🐓');
  if (/\b(poule|poules)\b/.test(normFr)) add('🐔');
  if (/\b(vache|veau|boeuf)\b/.test(normFr)) add('🐄');
  if (/\b(mouton|moutons|brebis|agneau)\b/.test(normFr)) add('🐑');
  if (/\b(loup|chacal)\b/.test(normFr)) add('🐺');
  if (/\b(aigle|faucon)\b/.test(normFr)) add('🦅');
  if (/\b(cheval|jument|poulain)\b/.test(normFr)) add('🐎');
  if (/\b(ane|anesse)\b/.test(normFr)) add('🫏');
  if (/\b(lapin|lapine)\b/.test(normFr)) add('🐇');
  if (/\b(sanglier|porc|cochon)\b/.test(normFr)) add('🐗');
  if (/\b(oiseau|oiseaux)\b/.test(normFr)) add('🐦');
  if (/\b(colombe|pigeon)\b/.test(normFr)) add('🕊️');
  if (/\b(hibou|chouette)\b/.test(normFr)) add('🦉');
  if (/\b(poisson|poissons)\b/.test(normFr)) add('🐟');
  if (/\b(mouche|mouches)\b/.test(normFr)) add('🪰');
  if (/\b(abeille|abeilles)\b/.test(normFr)) add('🐝');
  if (/\b(fourmi|fourmis)\b/.test(normFr)) add('🐜');
  if (/\b(papillon)\b/.test(normFr)) add('🦋');
  if (/\b(araignee)\b/.test(normFr)) add('🕷️');
  if (/\b(souris|rat)\b/.test(normFr)) add('🐭');

  // Food & Drinks
  if (/\b(pain|galette)\b/.test(normFr)) { add('🥖'); add('🫓'); }
  if (/\b(huile|olive|olivier)\b/.test(normFr)) { add('🫒'); add('🫗'); }
  if (/\b(couscous|plat|tajine)\b/.test(normFr)) { add('🍲'); add('🍽️'); }
  if (/\b(lait|lben)\b/.test(normFr)) add('🥛');
  if (/\b(miel)\b/.test(normFr)) add('🍯');
  if (/\b(cafe|expresso)\b/.test(normFr)) add('☕');
  if (/\b(the|infusion)\b/.test(normFr)) add('🍵');
  if (/\b(eau|source)\b/.test(normFr)) { add('💧'); add('🧊'); add('⛲'); }
  if (/\b(viande|boeuf)\b/.test(normFr)) add('🥩');
  if (/\b(poulet|coquelet)\b/.test(normFr)) { add('🍗'); add('🐔'); }
  if (/\b(pomme)\b/.test(normFr)) add('🍎');
  if (/\b(figue|figues)\b/.test(normFr)) add('🫐');
  if (/\b(raisin)\b/.test(normFr)) add('🍇');
  if (/\b(datte|dattes)\b/.test(normFr)) add('🌴');
  if (/\b(oignon)\b/.test(normFr)) add('🧅');
  if (/\b(carotte)\b/.test(normFr)) add('🥕');
  if (/\b(tomate)\b/.test(normFr)) add('🍅');
  if (/\b(pomme de terre)\b/.test(normFr)) add('🥔');
  if (/\b(salade)\b/.test(normFr)) add('🥗');
  if (/\b(frite|frites)\b/.test(normFr)) add('🍟');
  if (/\b(sandwich|casse-croute)\b/.test(normFr)) add('🥪');
  if (/\b(pizza)\b/.test(normFr)) add('🍕');
  if (/\b(glace|cornet)\b/.test(normFr)) add('🍦');
  if (/\b(jus)\b/.test(normFr)) add('🧃');
  if (/\b(soda|canette|gazouz)\b/.test(normFr)) { add('🥤'); add('🥫'); }
  if (/\b(fromage)\b/.test(normFr)) add('🧀');
  if (/\b(piment)\b/.test(normFr)) add('🌶️');
  if (/\b(poivron)\b/.test(normFr)) add('🫑');
  if (/\b(crepe|baghrir)\b/.test(normFr)) add('🥞');
  if (/\b(beignet|beignets)\b/.test(normFr)) add('🍩');
  if (/\b(soupe|chorba)\b/.test(normFr)) add('🍲');
  if (/\b(assiette)\b/.test(normFr)) add('🍽️');
  if (/\b(orange|citron)\b/.test(normFr)) { add('🍊'); add('🍋'); }
  if (/\b(sel)\b/.test(normFr)) add('🧂');
  if (/\b(sucre|bonbon)\b/.test(normFr)) add('🍬');
  if (/\b(gateau|dessert)\b/.test(normFr)) add('🍰');
  if (/\b(menthe|persil|coriandre|thym|origan|basilic|laurier|herbe|epice|cumin|cannelle)\b/.test(normFr)) { add('🌿'); add('🍃'); add('🌱'); }

  // Nature, Elements & Weather
  if (/\b(soleil|chaleur)\b/.test(normFr)) add('☀️');
  if (/\b(lune|croissant)\b/.test(normFr)) add('🌙');
  if (/\b(etoile|etoiles)\b/.test(normFr)) add('⭐');
  if (/\b(montagne|sommet|cime)\b/.test(normFr)) { add('⛰️'); add('🏔️'); }
  if (/\b(riviere|mer|vague)\b/.test(normFr)) { add('🌊'); add('🏞️'); }
  if (/\b(arbre|foret|bois)\b/.test(normFr)) { add('🌳'); add('🌲'); }
  if (/\b(fleur|fleurs|rose)\b/.test(normFr)) { add('🌸'); add('🌺'); add('🌹'); }
  if (/\b(herbe|plante)\b/.test(normFr)) { add('🌿'); add('🌱'); }
  if (/\b(feu|cheminee|foyer|flamme)\b/.test(normFr)) add('🔥');
  if (/\b(pluie|averse)\b/.test(normFr)) add('🌧️');
  if (/\b(neige|givre|glace)\b/.test(normFr)) { add('❄️'); add('🧊'); }
  if (/\b(vent|tempete)\b/.test(normFr)) { add('💨'); add('🌪️'); }
  if (/\b(nuage|nuages)\b/.test(normFr)) add('☁️');
  if (/\b(eclair|foudre|orage)\b/.test(normFr)) { add('⚡'); add('⛈️'); }
  if (/\b(terre|monde)\b/.test(normFr)) add('🌍');
  if (/\b(pierre|caillou|rocher)\b/.test(normFr)) add('🪨');

  // Body & Health
  if (/\b(coeur|amour)\b/.test(normFr)) { add('❤️'); add('💖'); }
  if (/\b(main|mains)\b/.test(normFr)) { add('✋'); add('🤝'); }
  if (/\b(doigt|doigts)\b/.test(normFr)) { add('☝️'); add('🖐️'); }
  if (/\b(ongle|ongles)\b/.test(normFr)) add('💅');
  if (/\b(pied|pieds|pas)\b/.test(normFr)) add('🦶');
  if (/\b(genou|genoux)\b/.test(normFr)) add('🦵');
  if (/\b(yeux|oeil|regard)\b/.test(normFr)) { add('👀'); add('👁️'); }
  if (/\b(bouche|levres)\b/.test(normFr)) add('👄');
  if (/\b(langue)\b/.test(normFr)) add('👅');
  if (/\b(oreille|oreilles)\b/.test(normFr)) add('👂');
  if (/\b(nez)\b/.test(normFr)) add('👃');
  if (/\b(dent|dents)\b/.test(normFr)) add('🦷');
  if (/\b(tete|visage)\b/.test(normFr)) add('👤');
  if (/\b(cheveu|cheveux|poil)\b/.test(normFr)) add('💇');
  if (/\b(ventre|estomac)\b/.test(normFr)) { add('🫄'); add('🤢'); }
  if (/\b(poumon|poumons)\b/.test(normFr)) add('🫁');
  if (/\b(os)\b/.test(normFr)) add('🦴');
  if (/\b(peau)\b/.test(normFr)) add('🧴');
  if (/\b(gorge|cou)\b/.test(normFr)) { add('🗣️'); add('🧣'); }
  if (/\b(bras|muscle|force)\b/.test(normFr)) add('💪');
  if (/\b(rhume|grippe|eternuer|eternuement)\b/.test(normFr)) { add('🤧'); add('🤒'); }
  if (/\b(toux|tousser)\b/.test(normFr)) { add('🗣️'); add('😷'); }
  if (/\b(fievre|chaud)\b/.test(normFr)) { add('🌡️'); add('🤒'); }
  if (/\b(douleur|mal|mal a|mal au)\b/.test(normFr)) { add('🤕'); add('⚡'); }
  if (/\b(blessure|plaie|coupure)\b/.test(normFr)) { add('🩸'); add('🩹'); }
  if (/\b(sang)\b/.test(normFr)) add('🩸');
  if (/\b(medicament|remede|pilule)\b/.test(normFr)) { add('💊'); add('🌿'); }
  if (/\b(pansement|bandage|compresse)\b/.test(normFr)) add('🩹');
  if (/\b(piqure|vaccin)\b/.test(normFr)) add('💉');
  if (/\b(hopital|clinique)\b/.test(normFr)) add('🏥');
  if (/\b(pharmacie)\b/.test(normFr)) add('🏪');
  if (/\b(sante|malade|docteur|medecin|soignant)\b/.test(normFr)) { add('🩺'); add('👨‍⚕️'); add('🩹'); add('💊'); }

  // Clothes & Objects & Culture
  if (/\b(robe)\b/.test(normFr)) add('👗');
  if (/\b(pantalon)\b/.test(normFr)) add('👖');
  if (/\b(chaussure|chaussures)\b/.test(normFr)) add('👞');
  if (/\b(chemise)\b/.test(normFr)) add('👔');
  if (/\b(bijou|bijoux|bague)\b/.test(normFr)) { add('💍'); add('🪙'); }
  if (/\b(tapis|laine|tissage)\b/.test(normFr)) add('🧶');
  if (/\b(poterie|cruche|vase)\b/.test(normFr)) add('🏺');
  if (/\b(musique|chanson|melodie)\b/.test(normFr)) { add('🎵'); add('🥁'); }
  if (/\b(berbere|kabyle|tamazight|yaz)\b/.test(normFr) || /\b(tamazight|taqbaylit|amazigh)\b/.test(normKab)) add('ⵣ');

  // People & Family
  if (/\b(pere|papa)\b/.test(normFr)) add('👨');
  if (/\b(mere|maman)\b/.test(normFr)) add('👩');
  if (/\b(fils|garcon)\b/.test(normFr)) add('👦');
  if (/\b(fille)\b/.test(normFr)) add('👧');
  if (/\b(bebe|nourrisson)\b/.test(normFr)) add('👶');
  if (/\b(grand pere|vieux|sage)\b/.test(normFr)) add('👴');
  if (/\b(grand mere|vieille)\b/.test(normFr)) add('👵');
  if (/\b(famille)\b/.test(normFr)) add('👨‍👩‍👧');

  // Actions & Travel
  if (/\b(courir|course|marche)\b/.test(normFr)) add('🏃');
  if (/\b(partir|marcher|aller)\b/.test(normFr)) add('🚶');
  if (/\b(voiture|taxi|auto)\b/.test(normFr)) { add('🚗'); add('🚕'); }
  if (/\b(avion|voyage|vol)\b/.test(normFr)) add('✈️');
  if (/\b(bateau|navire)\b/.test(normFr)) add('⛵');
  if (/\b(train|gare)\b/.test(normFr)) add('🚆');
  if (/\b(maison|foyer|demeure)\b/.test(normFr)) { add('🏠'); add('🏡'); }
  if (/\b(porte|entree)\b/.test(normFr)) add('🚪');
  if (/\b(cle|cles)\b/.test(normFr)) add('🔑');
  if (/\b(livre|ecrire|lire|conte)\b/.test(normFr)) { add('📚'); add('📖'); add('✍️'); add('📜'); }

  // 5. Fill remaining suggestions from category-specific presets
  const catKey = word.type || 'Personnalisé';
  const catPresets = CATEGORY_ICON_PRESETS[catKey] || CATEGORY_ICON_PRESETS['Personnalisé'] || [];
  for (const icon of catPresets) {
    if (suggestions.length >= limit) break;
    add(icon);
  }

  // 6. Fill remaining from universal presets
  for (const icon of POPULAR_UNIVERSAL_ICONS) {
    if (suggestions.length >= limit) break;
    add(icon);
  }

  return suggestions.slice(0, limit);
}


/**
 * Returns a high-quality, contextual emoji icon that matches the word item.
 */
export function getWordIcon(word: WordItem | null): string | null {
  if (!word) return null;
  if (word.icon) return word.icon;

  const rawFr = word.fr;
  const rawKab = word.kab;
  const normFr = normalizeText(rawFr);
  const normKab = normalizeText(rawKab);

  // 1. Exact lookup with normalized French definition
  if (EXACT_ICON_MAP[normFr]) {
    return EXACT_ICON_MAP[normFr];
  }

  // 2. Exact lookup with normalized Kabyle term
  if (EXACT_ICON_MAP[normKab]) {
    return EXACT_ICON_MAP[normKab];
  }

  // 3. Clean sub-phrase / token matches
  // Try matching stripped parts (e.g., "conte / légende" -> "conte" or "legende")
  const subParts = rawFr.split(/[/(),]/).map(s => normalizeText(s)).filter(Boolean);
  for (const part of subParts) {
    if (EXACT_ICON_MAP[part]) {
      return EXACT_ICON_MAP[part];
    }
  }

  // 4. Keyword and boundary regex checks
  // Animals
  if (/\b(chien|chienne|chiot)\b/.test(normFr)) return '🐕';
  if (/\b(chat|chatte|chaton)\b/.test(normFr)) return '🐈';
  if (/\b(lion|lionne)\b/.test(normFr)) return '🦁';
  if (/\b(panthere|leopard)\b/.test(normFr)) return '🐆';
  if (/\b(gazelle|cerf|biche)\b/.test(normFr)) return '🦌';
  if (/\b(chameau|dromadaire)\b/.test(normFr)) return '🐪';
  if (/\b(singe)\b/.test(normFr)) return '🐒';
  if (/\b(serpent|vipere)\b/.test(normFr)) return '🐍';
  if (/\b(tortue)\b/.test(normFr)) return '🐢';
  if (/\b(grenouille)\b/.test(normFr)) return '🐸';
  if (/\b(coq|poulet)\b/.test(normFr)) return '🐓';
  if (/\b(poule|poules)\b/.test(normFr)) return '🐔';
  if (/\b(vache|veau|boeuf)\b/.test(normFr)) return '🐄';
  if (/\b(mouton|moutons|brebis|agneau)\b/.test(normFr)) return '🐑';
  if (/\b(loup|chacal)\b/.test(normFr)) return '🐺';
  if (/\b(aigle|faucon)\b/.test(normFr)) return '🦅';
  if (/\b(cheval|jument|poulain)\b/.test(normFr)) return '🐎';
  if (/\b(ane|anesse)\b/.test(normFr)) return '🫏';
  if (/\b(lapin|lapine)\b/.test(normFr)) return '🐇';
  if (/\b(sanglier|porc|cochon)\b/.test(normFr)) return '🐗';
  if (/\b(oiseau|oiseaux)\b/.test(normFr)) return '🐦';
  if (/\b(colombe|pigeon)\b/.test(normFr)) return '🕊️';
  if (/\b(hibou|chouette)\b/.test(normFr)) return '🦉';
  if (/\b(poisson|poissons)\b/.test(normFr)) return '🐟';
  if (/\b(mouche|mouches)\b/.test(normFr)) return '🪰';
  if (/\b(abeille|abeilles)\b/.test(normFr)) return '🐝';
  if (/\b(fourmi|fourmis)\b/.test(normFr)) return '🐜';
  if (/\b(papillon)\b/.test(normFr)) return '🦋';
  if (/\b(araignee)\b/.test(normFr)) return '🕷️';
  if (/\b(souris|rat)\b/.test(normFr)) return '🐭';

  // Body & Clothes
  if (/\b(pantalon)\b/.test(normFr)) return '👖';
  if (/\b(robe)\b/.test(normFr)) return '👗';
  if (/\b(chaussure|chaussures)\b/.test(normFr)) return '👞';
  if (/\b(chemise)\b/.test(normFr)) return '👔';
  if (/\b(chaussette|chaussettes)\b/.test(normFr)) return '🧦';
  if (/\b(bijou|bijoux|bague|collier)\b/.test(normFr)) return '💍';
  if (/\b(savon)\b/.test(normFr)) return '🧼';
  if (/\b(dent|dents)\b/.test(normFr)) return '🦷';
  if (/\b(peigne)\b/.test(normFr)) return '🪮';
  if (/\b(langue)\b/.test(normFr)) return '👅';
  if (/\b(orteil|orteils)\b/.test(normFr)) return '🦶';
  if (/\b(coeur)\b/.test(normFr)) return '❤️';
  if (/\b(main|mains)\b/.test(normFr)) return '✋';
  if (/\b(pied|pieds)\b/.test(normFr)) return '🦶';
  if (/\b(yeux|oeil)\b/.test(normFr)) return '👀';
  if (/\b(bouche)\b/.test(normFr)) return '👄';
  if (/\b(oreille|oreilles)\b/.test(normFr)) return '👂';
  if (/\b(nez)\b/.test(normFr)) return '👃';
  if (/\b(barbe)\b/.test(normFr)) return '🧔';
  if (/\b(bras)\b/.test(normFr)) return '💪';
  if (/\b(poitrine)\b/.test(normFr)) return '🫁';

  // House & Furniture
  if (/\b(maison)\b/.test(normFr)) return '🏠';
  if (/\b(porte)\b/.test(normFr)) return '🚪';
  if (/\b(cle|cles)\b/.test(normFr)) return '🔑';
  if (/\b(lit)\b/.test(normFr)) return '🛏️';
  if (/\b(miroir)\b/.test(normFr)) return '🪞';
  if (/\b(bouteille)\b/.test(normFr)) return '🍾';
  if (/\b(assiette)\b/.test(normFr)) return '🍽️';
  if (/\b(cuillere)\b/.test(normFr)) return '🥄';
  if (/\b(fourchette)\b/.test(normFr)) return '🍴';
  if (/\b(couteau)\b/.test(normFr)) return '🔪';
  if (/\b(mur)\b/.test(normFr)) return '🧱';
  if (/\b(toit)\b/.test(normFr)) return '🏠';
  if (/\b(feu|cheminee|foyer)\b/.test(normFr)) return '🔥';
  if (/\b(poterie|cruche|vase)\b/.test(normFr)) return '🏺';
  if (/\b(tapis|tissage)\b/.test(normFr)) return '🧶';

  // Nature & Weather & Food
  if (/\b(galette|aghroum)\b/.test(normFr) || /\b(aghroum)\b/.test(normKab)) return '🫓';
  if (/\b(couscous)\b/.test(normFr)) return '🍲';
  if (/\b(eau)\b/.test(normFr) || /\b(aman)\b/.test(normKab)) return '💧';
  if (/\b(lait)\b/.test(normFr) || /\b(ayefki)\b/.test(normKab)) return '🥛';
  if (/\b(miel)\b/.test(normFr) || /\b(tament)\b/.test(normKab)) return '🍯';
  if (/\b(pain)\b/.test(normFr)) return '🥖';
  if (/\b(sel)\b/.test(normFr)) return '🧂';
  if (/\b(sucre)\b/.test(normFr)) return '🍬';
  if (/\b(pomme)\b/.test(normFr)) return '🍎';
  if (/\b(figue|figues)\b/.test(normFr)) return '🫐';
  if (/\b(raisin)\b/.test(normFr)) return '🍇';
  if (/\b(oignon)\b/.test(normFr)) return '🧅';
  if (/\b(carotte)\b/.test(normFr)) return '🥕';
  if (/\b(viande)\b/.test(normFr) || /\b(aksum)\b/.test(normKab)) return '🥩';
  if (/\b(arbre)\b/.test(normFr)) return '🌳';
  if (/\b(soleil)\b/.test(normFr) || /\b(itij|itij)\b/.test(normKab)) return '☀️';
  if (/\b(lune)\b/.test(normFr) || /\b(aggur|ayur|tiziri)\b/.test(normKab)) return '🌙';
  if (/\b(etoile|etoiles)\b/.test(normFr) || /\b(itran|itri)\b/.test(normKab)) return '⭐';
  if (/\b(montagne|montagnes)\b/.test(normFr)) return '🏔️';
  if (/\b(pluie)\b/.test(normFr)) return '🌧️';
  if (/\b(nuage|nuages)\b/.test(normFr)) return '☁️';
  if (/\b(neige)\b/.test(normFr) || /\b(tagrest)\b/.test(normKab)) return '❄️';
  if (/\b(vent)\b/.test(normFr)) return '💨';
  if (/\b(fleur|fleurs)\b/.test(normFr) || /\b(tafsut)\b/.test(normKab)) return '🌸';
  if (/\b(foret)\b/.test(normFr)) return '🌲';
  if (/\b(olivier|olives|huile d olive)\b/.test(normFr) || /\b(azemmur|tazemmurt)\b/.test(normKab)) return '🫒';
  if (/\b(mer|riviere|ocean)\b/.test(normFr)) return '🌊';
  if (/\b(berbere|kabyle|tamazight|yaz)\b/.test(normFr) || /\b(tamazight|taqbaylit|amazigh)\b/.test(normKab)) return 'ⵣ';
  if (/\b(livre|ecrire|cahier|stylo|ecole)\b/.test(normFr)) return '📚';
  if (/\b(musique|chanson)\b/.test(normFr) || /\b(tizlit)\b/.test(normKab)) return '🎵';

  // Category fallbacks as ultimate safe guard
  if (word.type === 'Animaux') return '🐾';
  if (word.type === 'La Maison') return '🏠';
  if (word.type === 'Le Corps & Soins') return '🧍';
  if (word.type === 'Nourriture') return '🍽️';
  if (word.type === 'Nature & Météo') return '🌿';
  if (word.type === 'Nombres & Quantité') return '🔢';
  if (word.type === 'Couleurs') return '🎨';
  if (word.type === 'Jeux & Transports') return '🚗';
  if (word.type === 'La Famille') return '👨‍👩‍👧';
  if (word.type === 'Histoires & École') return '📚';
  if (word.type === 'Le Temps') return '⏳';
  if (word.type === 'Émotions & Sensations') return '💜';
  if (word.type === 'Phrases & Quotidien') return '💬';
  if (word.type === 'Salutations & Politesse') return '👋';
  if (word.type === 'Pronoms & Personnes') return '👤';
  if (word.type === 'Questions & Réponses') return '❓';
  if (word.type === 'Actions & Verbes' || word.type === "Verbes d'Action" || word.type === 'Verbes') return '🏃';
  if (word.type === 'Verbes de Communication' || word.type === 'Pensée & Communication') return '🗣️';
  if (word.type === 'Verbes au Passé') return '🕰️';
  if (word.type === 'Verbes au Futur & Présent') return '⚡';
  if (word.type === 'Mots de liaison') return '🔗';

  return '✨';
}
